import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Moon, Sun, Languages, LogIn } from "lucide-react";
import { useApp } from "@/context/AppProviders";
import { PORTAL_LOGIN_URL } from "@/config";

const trackPortalLogin = () => window.posthog?.capture?.("Portal Login Click");

const Header = () => {
  const { t, lang, toggleLang, theme, toggleTheme } = useApp();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#features", label: t.nav.features },
    { href: "#disciplines", label: t.nav.disciplines },
    { href: "#ai", label: t.nav.ai },
    { href: "#pricing", label: t.nav.pricing },
    { href: "#academy", label: t.nav.academy },
    { href: "#trial", label: t.nav.trial },
    { href: "#faq", label: t.nav.faq },
  ];

  const scrollTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = `/${href}`;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 border-b ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Brand */}
          <a
            href="#hero"
            onClick={(e) => scrollTo(e, "#hero")}
            data-testid="brand-logo"
            className="flex items-center gap-2 group"
          >
            <img
              src="/logo.ico"
              alt="SecureMaintPro"
              className="h-8 w-8 rounded-sm object-contain"
            />
            <span className="font-heading text-lg font-bold tracking-tight">
              SecureMaintPro
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                data-testid={`nav-link-${link.href.slice(1)}`}
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/shop"
              data-testid="nav-link-shop"
              onClick={() => setOpen(false)}
              className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              {t.shop.nav}
            </Link>
          </nav>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              aria-label="Toggle language"
              className="flex items-center gap-1.5 h-9 px-3 rounded-sm border border-border bg-card text-xs font-mono font-medium hover:bg-secondary transition-colors duration-200"
            >
              <Languages className="h-3.5 w-3.5" />
              <span className={lang === "nl" ? "text-blue-500" : "text-muted-foreground"}>NL</span>
              <span className="text-border">/</span>
              <span className={lang === "en" ? "text-blue-500" : "text-muted-foreground"}>EN</span>
            </button>

            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-card hover:bg-secondary transition-colors duration-200"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>

            <a
              href={PORTAL_LOGIN_URL}
              onClick={trackPortalLogin}
              data-testid="header-portal-login"
              aria-label="Inloggen op klantportaal"
              className="hidden sm:inline-flex h-9 items-center gap-1.5 rounded-sm bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-200"
            >
              <LogIn className="h-4 w-4" />
              {t.portal.navLabel}
            </a>

            <a
              href="#contact"
              onClick={(e) => scrollTo(e, "#contact")}
              data-testid="header-cta"
              className="hidden lg:inline-flex h-9 items-center rounded-sm border border-border bg-card px-4 text-sm font-medium text-foreground hover:bg-secondary transition-colors duration-200"
            >
              {t.nav.cta}
            </a>

            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="md:hidden flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-card"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl"
        >
          <nav className="flex flex-col px-6 py-4 gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className="px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              data-testid="nav-link-shop-mobile"
              className="px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t.shop.nav}
            </Link>
            <a
              href="#contact"
              onClick={(e) => scrollTo(e, "#contact")}
              className="mt-2 inline-flex h-10 items-center justify-center rounded-sm border border-border bg-card px-4 text-sm font-medium text-foreground"
            >
              {t.nav.cta}
            </a>
            <a
              href={PORTAL_LOGIN_URL}
              onClick={() => {
                trackPortalLogin();
                setOpen(false);
              }}
              data-testid="header-portal-login-mobile"
              aria-label="Inloggen op klantportaal"
              className="mt-2 inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-sm bg-blue-600 px-4 text-sm font-medium text-white"
            >
              <LogIn className="h-4 w-4" />
              {t.portal.navLabel}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
